import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Address } from "../types";

interface User {
  _id: string;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImage?: string;
  role: string;
  accountRole?: string;
  permissions: string[];
  accountId?: string;
  currentAccountId?: string;
  impersonating?: boolean;
  originalAdmin?: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  address?: Address;
  phone?: string;
  needs2FASetup?: boolean;
  smsPhoneNumber?: string;
  lastLogin?: Date | string;
  isPending?: boolean;
  threePMUserId?: string; // User ID from 3PM Auth (indicates SSO login)
}

interface UserAccount {
  accountId: string;
  companyName: string;
  logo?: string;
  role: string;
  permissions: string[];
  joinedAt: Date;
  isCurrent: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyCode: (
    email: string,
    code: string,
  ) => Promise<{
    requires2FA?: boolean;
    hasAuthenticator?: boolean;
    hasSMS?: boolean;
    needs2FASetup?: boolean;
  }>;
  verify2FA: (
    email: string,
    code: string,
    method: "totp" | "sms" | "backup",
  ) => Promise<void>;
  sendSMS2FACode: (email: string) => Promise<void>;
  signup: (
    email: string,
    firstName: string,
    lastName: string,
    companyName: string,
    address: any,
  ) => Promise<void>;
  verifySignupCode: (
    email: string,
    code: string,
    firstName?: string,
    lastName?: string,
    companyName?: string,
    address?: any,
  ) => Promise<{ needs2FASetup?: boolean }>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getUserAccounts: () => Promise<UserAccount[]>;
  switchAccount: (accountId: string) => Promise<void>;
  handleCallback: (
    guid: string,
    returnUrl: string,
  ) => Promise<{ onboardingComplete: boolean; returnUrl: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const refreshUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        localStorage.setItem("user", JSON.stringify(data.user));
      }
    } catch (error) {
      // Don't log out on network errors - user might be offline
      const isNetworkError =
        error instanceof TypeError ||
        (error as any)?.message?.includes("Failed to fetch");
      if (!isNetworkError) {
        console.error("User refresh failed:", error);
      }
    }
  };

  const login = async (email: string) => {
    try {
      // First check if email exists
      const checkResponse = await fetch(`${apiUrl}/api/auth/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const checkData = await checkResponse.json();

      if (!checkData.exists) {
        // User doesn't exist - prompt for signup
        const error: any = new Error(
          "No Account but would you like to sign up?",
        );
        error.requiresSignup = true;
        throw error;
      }

      // User exists - send verification code
      const response = await fetch(
        `${apiUrl}/api/auth/send-verification-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to send verification code");
      }

      // Success - no popup needed, handled in component
    } catch (error: any) {
      // If it's already an error with requiresSignup, re-throw it
      if (error.requiresSignup) {
        throw error;
      }

      // Handle network errors or JSON parse errors
      if (
        error instanceof TypeError ||
        error.message?.includes("Failed to fetch")
      ) {
        throw new Error("Network error. Please check your connection.");
      }

      console.error("Login error:", error);
      throw error;
    }
  };

  const verifyCode = async (
    email: string,
    code: string,
  ): Promise<{
    requires2FA?: boolean;
    hasAuthenticator?: boolean;
    hasSMS?: boolean;
    needs2FASetup?: boolean;
  }> => {
    try {
      // Trim and normalize the code before sending
      const normalizedCode = String(code).trim().replace(/\s/g, "");

      const response = await fetch(`${apiUrl}/api/auth/verify-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code: normalizedCode }),
      });

      const data = await response.json();
      if (data.success) {
        // Check if 2FA is required
        if (data.requires2FA) {
          return {
            requires2FA: true,
            hasAuthenticator: data.hasAuthenticator,
            hasSMS: data.hasSMS,
          };
        }

        // Normal login - no 2FA
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        return { needs2FASetup: data.needs2FASetup || false };
      } else {
        throw new Error(data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error("Verify code error:", error);
      throw error;
    }
  };

  const verify2FA = async (
    email: string,
    code: string,
    method: "totp" | "sms" | "backup",
  ) => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/2fa/verify-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code, method }),
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
      } else {
        throw new Error(data.message || "Invalid 2FA code");
      }
    } catch (error) {
      console.error("2FA verify error:", error);
      throw error;
    }
  };

  const sendSMS2FACode = async (email: string) => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/2fa/send-sms-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to send SMS code");
      }
    } catch (error) {
      console.error("Send SMS code error:", error);
      throw error;
    }
  };

  const signup = async (
    email: string,
    firstName: string,
    lastName: string,
    companyName: string,
    address: any,
  ) => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          companyName,
          address,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to create account");
      }
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  const verifySignupCode = async (
    email: string,
    code: string,
    firstName?: string,
    lastName?: string,
    companyName?: string,
    address?: any,
  ): Promise<{ needs2FASetup?: boolean }> => {
    try {
      // Trim and normalize the code before sending (consistent with verifyCode)
      const normalizedCode = String(code).trim().replace(/\s/g, "");

      const body: any = { email, code: normalizedCode };
      if (firstName) body.firstName = firstName;
      if (lastName) body.lastName = lastName;
      if (companyName) body.companyName = companyName;
      if (address) body.address = address;

      const response = await fetch(`${apiUrl}/api/auth/verify-signup-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        return { needs2FASetup: data.needs2FASetup || false };
      } else {
        throw new Error(data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error("Verify signup code error:", error);
      throw error;
    }
  };

  const logout = async () => {
    // Clear user state and localStorage
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    
    // Set a flag to indicate we're logging out
    // This prevents auto-login if we get redirected back to SSO callback
    sessionStorage.setItem("isLoggingOut", "true");

    // Submit form POST to backend logout endpoint
    // Backend handles 3PM Auth signout and redirects to 3PM Auth login
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${apiUrl}/api/auth/logout`;
    form.style.display = "none";
    
    document.body.appendChild(form);
    form.submit();
  };

  const impersonate = async (userId: string) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/auth/impersonate/${userId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        console.log('[AuthContext] Impersonation successful, user data:', data.user);
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Comprehensive context reload after impersonation
        // Use Promise.all to reload in parallel for better performance
        await Promise.all([
          // Reload permissions
          typeof (window as any).__refetchPermissions === "function"
            ? (window as any).__refetchPermissions()
            : Promise.resolve(),
          // Reload navigation
          typeof (window as any).__reloadNavigation === "function"
            ? (window as any).__reloadNavigation()
            : Promise.resolve(),
        ]);

        // Force a full page reload to dashboard to ensure clean state
        // This ensures all components re-mount with the new user context
        window.location.href = "/dashboard";
      } else {
        throw new Error(data.message || "Failed to impersonate user");
      }
    } catch (error) {
      console.error("Impersonate error:", error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/auth/stop-impersonation`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setUser(data.user);
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Comprehensive context reload after stopping impersonation
        await Promise.all([
          // Reload permissions
          typeof (window as any).__refetchPermissions === "function"
            ? (window as any).__refetchPermissions()
            : Promise.resolve(),
          // Reload navigation
          typeof (window as any).__reloadNavigation === "function"
            ? (window as any).__reloadNavigation()
            : Promise.resolve(),
        ]);

        // Force a full page reload to dashboard to ensure clean state
        window.location.href = "/dashboard";
      } else {
        throw new Error(data.message || "Failed to stop impersonation");
      }
    } catch (error) {
      console.error("Stop impersonation error:", error);
      throw error;
    }
  };

  const getUserAccounts = async (): Promise<UserAccount[]> => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/users/me/accounts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to get user accounts");
      }
    } catch (error) {
      console.error("Get user accounts error:", error);
      throw error;
    }
  };

  const switchAccount = async (accountId: string) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/users/me/switch-account/${accountId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        // Update token immediately
        localStorage.setItem("token", data.token);
        
        // Optimistically update user state with account info from response
        // This provides instant feedback before page reload
        if (user) {
          setUser({
            ...user,
            accountId: data.account.accountId,
            currentAccountId: data.account.accountId,
          });
        }
        
        // Reload page to refresh all context-dependent data (permissions, navigation, etc.)
        // Using a small delay to ensure state updates are visible
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        throw new Error(data.message || "Failed to switch account");
      }
    } catch (error) {
      console.error("Switch account error:", error);
      throw error;
    }
  };

  const handleCallback = async (
    guid: string,
    returnUrl: string,
  ): Promise<{ onboardingComplete: boolean; returnUrl: string }> => {
    try {
      const response = await fetch(`${apiUrl}/api/sso/callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ guid }),
      });

      const data = await response.json();

      if (!data.success) {
        // Check if error is about organization/tenant membership
        const errorMessage = data.error || "Authentication failed";
        if (
          errorMessage.includes("organization") ||
          errorMessage.includes("part of") ||
          errorMessage.includes("tenant")
        ) {
          const error: any = new Error(errorMessage);
          error.requiresOrganization = true;
          throw error;
        }
        throw new Error(errorMessage);
      }

      // Store token and user
      localStorage.setItem("token", data.token);
      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));

      // Return onboarding status (needsAccountSetup is opposite of onboardingComplete)
      return {
        onboardingComplete: !data.needsAccountSetup,
        returnUrl,
      };
    } catch (error) {
      console.error("Handle callback error:", error);
      throw error;
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        // Try to restore user from localStorage first (for offline support)
        const savedUser = localStorage.getItem("user");
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            setUser(userData);
          } catch (e) {
            console.warn("Failed to parse saved user data:", e);
          }
        }

        try {
          // Verify token with backend (only if online)
          if (navigator.onLine) {
            const response = await fetch(`${apiUrl}/api/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            const data = await response.json();
            if (data.success) {
              setUser(data.user);
              // Save user data to localStorage for offline use
              localStorage.setItem("user", JSON.stringify(data.user));
            } else {
              // Token is invalid (401/403), clear it
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              setUser(null);
            }
          } else {
            // Offline: keep existing user from localStorage if available
            console.log("[Auth] Offline - using cached user data");
          }
        } catch (error) {
          // Network error (likely offline) - don't log out, keep token and user
          const isNetworkError =
            error instanceof TypeError ||
            (error as any)?.message?.includes("Failed to fetch") ||
            (error as any)?.message?.includes("NetworkError");

          if (isNetworkError) {
            console.log(
              "[Auth] Network error - keeping user logged in for offline mode",
            );
            // User stays logged in with cached data
          } else {
            // Other errors: only clear if it's an auth error (401/403)
            console.error("Token verification failed:", error);
            // Don't remove token on unknown errors - might be temporary server issue
          }
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        verifyCode,
        verify2FA,
        sendSMS2FACode,
        signup,
        verifySignupCode,
        impersonate,
        stopImpersonation,
        refreshUser,
        getUserAccounts,
        switchAccount,
        handleCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
